<template>
  <div
    ref="leftSidebarEl"
    class="container"
    :style="{
      minWidth: `${minWidth}px`,
      ...(maxWidth !== undefined ? { maxWidth: `${maxWidth}px` } : {}),
    }"
  >
    <BasePaneHeader v-if="$slots.heading" has-nav>
      <slot name="heading" />
    </BasePaneHeader>
    <div class="main">
      <slot />
    </div>
    <div v-if="isResizable" class="handle" @mousedown="startResize" />
  </div>
</template>
<script lang="ts">
import { defineComponent, onMounted, ref, toRefs } from "vue";

import BasePaneHeader from "@/components/base/BasePaneHeader.vue";
import * as inject from "@/components/inject";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { useReleaser } from "@/ref";

export default defineComponent({
  components: {
    BasePaneHeader,
  },
  props: {
    storageKey: {
      type: String,
      required: true,
    },
    isResizable: {
      type: Boolean,
      default: false,
    },
    minWidth: {
      type: Number,
      required: false,
      default: 252,
    },
    maxWidth: {
      type: Number,
      required: false,
    },
  },
  emits: {
    resized: (_width: number) => true,
  },
  setup(props, { emit }) {
    const { storageKey, minWidth, maxWidth, isResizable } = toRefs(props);
    const leftSidebarEl = ref<HTMLElement>();

    // Load & sync width with the browser's local storage.
    const profile = inject.profile();
    const releaser = useReleaser();
    const { syncState } = useBrowserStore(profile.value.user.accountId);
    const width = releaser.add(
      syncState<number>(storageKey.value, minWidth.value)
    );

    const resize = (event: MouseEvent) => {
      if (!leftSidebarEl.value || !isResizable.value) return;
      const rect = leftSidebarEl.value.getBoundingClientRect();
      // Handle sits on the inline-end edge; grow toward inline-end.
      const rawWidth =
        document.documentElement.dir === "rtl"
          ? rect.right - event.clientX
          : event.clientX - rect.left;
      setWidth(rawWidth);
    };
    const setWidth = (value: number) => {
      if (leftSidebarEl.value) {
        const minConstrainedWidth = Math.max(minWidth.value, value);
        const constrainedWidth =
          maxWidth.value !== undefined
            ? Math.min(maxWidth.value, minConstrainedWidth)
            : minConstrainedWidth;
        leftSidebarEl.value.style.width = `${constrainedWidth}px`;
        emit("resized", leftSidebarEl.value?.clientWidth);
        width.value = constrainedWidth;
      }
    };
    onMounted(() => {
      setWidth(width.value);
    });
    const stopResize = () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResize);
    };
    const startResize = (event: MouseEvent) => {
      if (!isResizable.value) return;
      event.preventDefault();
      window.addEventListener("mousemove", resize);
      window.addEventListener("mouseup", stopResize);
    };
    return {
      leftSidebarEl,
      startResize,
    };
  },
});
</script>
<style lang="scss" scoped>
.container {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  box-sizing: border-box;
  min-width: 252px;
  @include themed() {
    border-inline-end: 1px solid t($grey-15);
  }
  @media screen and (max-width: 1480px) {
    max-width: 352px;
  }
  @media screen and (min-width: 1480px) {
    max-width: 600px;
  }

  .main {
    display: flex;
    flex: 1;
    max-height: 100vh;
  }

  .handle {
    position: absolute;
    top: 0;
    bottom: 0;
    inset-inline-end: -6px;
    width: 10px;
    z-index: $z-index-content-high;
    cursor: col-resize;
  }
}
</style>
